/**
 * Die wiederkehrenden Bauteile des Saals: Urkunde, Stempel, Haarlinie,
 * Leerzustand, Fehlermeldung. Alles Kleine, das sonst siebenmal entstünde.
 */
import type { ReactNode } from 'react'
import type { PostenStatus } from '../api/typen.js'

export function Haarlinie({ className = '' }: { className?: string }) {
  return <hr className={`haarlinie ${className}`} />
}

export function Abschnitt({
  titel,
  children,
  aktion,
}: {
  titel: string
  children: ReactNode
  aktion?: ReactNode
}) {
  return (
    <section className="pt-8">
      <div className="flex items-baseline justify-between gap-3">
        <p className="protokoll">{titel}</p>
        {aktion}
      </div>
      <div className="mt-3">{children}</div>
    </section>
  )
}

export function Stempel({ status }: { status: PostenStatus | 'anhaengig' | 'vollstreckt' }) {
  const klasse =
    status === 'getilgt'
      ? 'stempel-getilgt'
      : status === 'erlassen'
        ? 'stempel-getilgt'
        : status === 'vollstreckt'
          ? 'stempel-vollstreckt'
          : 'stempel-offen'
  const wort =
    status === 'getilgt'
      ? 'Getilgt'
      : status === 'erlassen'
        ? 'Erlassen'
        : status === 'vollstreckt'
          ? 'Vollstreckt'
          : status === 'anhaengig'
            ? 'Anhängig'
            : 'Offen'
  return <span className={`stempel shrink-0 ${klasse}`}>{wort}</span>
}

export function Urkunde({
  randnummer,
  children,
}: {
  randnummer?: string | number
  children: ReactNode
}) {
  return (
    <article className="urkunde">
      <span className="randnummer" aria-hidden="true">
        {randnummer !== undefined && (
          <>
            Rn.
            <br />
            {randnummer}
          </>
        )}
      </span>
      <div className="min-w-0">{children}</div>
    </article>
  )
}

export function LeererZustand({ text }: { text: string }) {
  return (
    <p className="border border-eiche bg-bank px-4 py-6 text-center text-sm text-akte/70">{text}</p>
  )
}

export function Fehlermeldung({ text }: { text: string | null }) {
  if (!text) return null
  return (
    <p role="alert" className="mt-3 border border-siegel/60 bg-siegel/10 px-3 py-2 text-sm text-akte">
      {text}
    </p>
  )
}

export function Laufvermerk() {
  return <p className="protokoll py-8 text-center normal-case">Die Akte wird geholt …</p>
}

/** Primäraktion — Messing auf Leder. */
export function Amtsknopf({
  children,
  onClick,
  disabled = false,
  type = 'button',
  variante = 'messing',
}: {
  children: ReactNode
  onClick?: () => void
  disabled?: boolean
  type?: 'button' | 'submit'
  variante?: 'messing' | 'leder' | 'siegel'
}) {
  const farben =
    variante === 'messing'
      ? 'bg-messing text-saal hover:bg-lampe disabled:bg-eiche disabled:text-akte/40'
      : variante === 'siegel'
        ? 'bg-siegel/90 text-lampe hover:bg-siegel disabled:bg-eiche disabled:text-akte/40'
        : 'border border-messing/50 text-messing hover:border-messing hover:text-lampe disabled:border-eiche disabled:text-akte/40'
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`px-4 py-2.5 font-sans text-sm font-semibold tracking-wide uppercase transition-colors ${farben}`}
    >
      {children}
    </button>
  )
}

export function Feld({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <label className="block">
      <span className="protokoll">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  )
}

/** Basis ohne Breite — `w-full` an der Verwendungsstelle setzen, wo gewünscht. */
export const eingabeKlasse =
  'border border-eiche bg-saal px-3 py-2.5 text-akte placeholder:text-akte/35 focus:border-messing focus:outline-none'
